import React, { useState } from "react";
import { Tooltip, IconButton, Button, TextField, Box } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import dayjs from "dayjs";

import { getSiteById, SiteByIdDTO } from "api";
import { InfoIcon, EsgEditIcon, CheckIcon, CrossIcon } from "assets";
import { getUTCDateFormat, isNullOrUndefined, checkNullValue } from "utils";
import { COLORS, EMPTY_CELL_VALUE } from "components";

import CustomSnackbar from "./components/CustomSnackBar";

export const collectorsColumns = (
  siteData: SiteByIdDTO,
  updateCollectorRequest: any,
  notify: any,
  setIsEditUnitCost?: React.Dispatch<React.SetStateAction<boolean>>,
  isEditUnitCost?: boolean,
  setShowSideInfoDrawer?: React.Dispatch<React.SetStateAction<boolean>>,
  setUnitCostData?: React.Dispatch<React.SetStateAction<any>>,
  UnitCostData?: any,
  setOpenSnackBar?: React.Dispatch<React.SetStateAction<boolean>>,
  openSnackBar?: boolean,
  setSiteData?: React.Dispatch<React.SetStateAction<SiteByIdDTO | undefined>>,
): MRT_ColumnDef<any>[] => [
    {
      accessorKey: "scope",
      header: "GHG Emission",
      Cell: ({ row }: any) => {
        const value = row.original.scope;
        if (isNullOrUndefined(value)) {
          return EMPTY_CELL_VALUE;
        }
        if (value === "NO_SCOPE") {
          return "No Scope";
        }
        return value;
      },
      size: 10,
    },
    {
      accessorKey: "utilityTypeName",
      header: "Utility Type",
      size: 10,
    },
    {
      accessorKey: "utilitySubType",
      header: "Utility Subtype",
      size: 10,
    },
    {
      accessorKey: "accountNumber",
      header: "Account Number",
      size: 10,
    },
    {
      accessorKey: "meterNumber",
      header: "Meter Number",
      size: 10,
    },
    {
      accessorKey: "providerName",
      header: "Utility Provider",
      size: 10,
    },
    {
      accessorKey: "allowedDocuments",
      header: "Document Type",
      size: 10,
    },
    {
      accessorKey: "startDate",
      header: "Start Date",
      size: 10,
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value) || value === 0) {
          return EMPTY_CELL_VALUE;
        }
        return dayjs(value as string)
          .utc()
          .format(getUTCDateFormat());
      },
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      size: 10,
      Cell: ({ cell }: any) => {
        const value = cell.getValue();
        if (isNullOrUndefined(value) || value === 0) {
          return EMPTY_CELL_VALUE;
        }
        return dayjs(value as string)
          .utc()
          .format(getUTCDateFormat());
      },
    },
    {
      accessorKey: "frequency",
      header: "Frequency",
      size: 10,
    },
    {
      accessorKey: "consumptionUnit",
      header: "Consumption Unit",
      size: 10,
    },
    {
      accessorKey: "unitCost",
      header: "Unit Cost",
      size: 10,
      Header: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span style={{ fontSize: "14px" }}>Unit Cost</span>

          <Tooltip
            title="This value is computed by dividing your overall bill cost by your complete usage and requires manual input"
            placement="top"
          >
            <IconButton
              aria-label="info"
              size="small"
              sx={{ maxHeight: "23px", maxWidth: "23px" }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
      Cell: ({ row }: any) => {
        const [unitCostValue, setUnitCostValue] = useState(
          row.original.unitCost || "",
        );

        const handleEditUnitCost = () => {
          setIsEditUnitCost?.(true);
          setShowSideInfoDrawer?.(true);
          setUnitCostData?.(row.original);
        };

        const handleSaveUnitCost = async () => {
          if (siteData) {
            const updatedCollectorData: any = {
              accountNumber: checkNullValue(row.original.accountNumber),
              allowedDocuments: Array.isArray(row.original.allowedDocuments)
                ? checkNullValue(row.original.allowedDocuments)
                : [row.original.allowedDocuments],
              consumptionUnit: checkNullValue(row.original.consumptionUnit),
              currency: checkNullValue(row.original.currency),
              frequency: checkNullValue(row.original.frequency),
              meterNumber: checkNullValue(row.original.meterNumber),
              providerName: checkNullValue(row.original.providerName),
              notes: checkNullValue(row.original.notes),
              scope: checkNullValue(row.original.scope),
              unitCost: unitCostValue,
              isSpendBased: checkNullValue(row.original.spendBased),
              siteId: siteData.id,
              id: checkNullValue(row.original.id),
              utilityTypeId: checkNullValue(row.original.utilityTypeId),
            };
            await updateCollectorRequest(updatedCollectorData, {
              onError: () => {
                notify.error(
                  `Some error has happened while editing Collector!`,
                );
              },
              onSuccess: () => {
                setOpenSnackBar?.(true);
                setIsEditUnitCost?.(false);
                setShowSideInfoDrawer?.(false);
                if (siteData) {
                  getSiteById(siteData?.id)
                    .then((currentSiteData) => {
                      setSiteData?.(currentSiteData);
                    })
                    .catch((error) => {
                      // eslint-disable-next-line
                      console.error("Error fetching site data:", error);
                    });
                }
              },
            });
          }
        };

        const handleCancelEdit = () => {
          setIsEditUnitCost?.(false);
          setShowSideInfoDrawer?.(false);
        };

        return (
          <>
            {row.original.spendBased && (
              <>
                {isEditUnitCost && UnitCostData.id === row.original.id ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      boxSizing: "border-box",
                    }}
                  >
                    <TextField
                      sx={{ width: "140px" }}
                      variant="outlined"
                      value={unitCostValue}
                      onChange={(e) => setUnitCostValue(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <div style={{ display: "flex" }}>
                            <IconButton
                              aria-label="Save"
                              size="small"
                              sx={{
                                maxHeight: "24px",
                                maxWidth: "24px",
                                color: "green",
                              }}
                              onClick={handleSaveUnitCost}
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              aria-label="Cancel"
                              size="small"
                              sx={{
                                maxHeight: "24px",
                                maxWidth: "24px",
                                color: "red",
                              }}
                              onClick={handleCancelEdit}
                            >
                              <CrossIcon />
                            </IconButton>
                          </div>
                        ),
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      maxWidth: row.original.unitCost !== "0" ? "80px" : "20px",
                    }}
                  >
                    {row.original.unitCost === null ||
                      row.original.unitCost === "0" ? (
                      <Tooltip title="Edit Unit Cost">
                        <IconButton
                          aria-label="Edit"
                          size="small"
                          sx={{ maxHeight: "24px", maxWidth: "24px" }}
                          onClick={handleEditUnitCost}
                        >
                          <EsgEditIcon color={COLORS.Romance} />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title={row.original.unitCost}>
                          <Box
                            sx={{
                              alignItems: "center",
                              width: "100px",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                            }}
                          >
                            <span>{row.original.unitCost}</span>
                          </Box>
                        </Tooltip>
                        {row.original.enabled && (
                          <Button
                            sx={{
                              borderRadius: "4px",
                              height: "20.5px",
                              minWidth: "38px",
                              padding: "1px",
                              fontSize: "12px",
                            }}
                            variant="contained"
                            onClick={handleEditUnitCost}
                          >
                            Edit
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
            {openSnackBar && (
              <CustomSnackbar
                open={openSnackBar}
                message="Your Unit Cost has now changed and will now apply to all future bills!"
                onClose={() => setOpenSnackBar?.(false)}
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: "currency",
      header: "Currency",
      size: 10,
    },
    {
      accessorKey: "notes",
      header: "Notes",
    },
  ];